/**
 * PreVamGateView — Sprint 11 S11.2 (motor v8 §1).
 *
 * Vista del plan cuando el gate "plan oculto hasta TEST-VAM completado"
 * está activo (`planGate.evaluatePlanGate`). En lugar de renderizar
 * WeekView + navegación entre semanas, mostramos SOLO la card del
 * TEST-VAM como puerta de entrada, con un mensaje explicativo.
 *
 * Decisiones de diseño:
 * - Reusa `SessionCard` (mismo componente que el plan normal usa)
 *   para que el atleta vea la sesión TEST-VAM en su forma canónica
 *   (con badge "TEST", protocolo, etc.). La card es clickeable y
 *   redirige a `/plan/sesion/<id>` igual que en plan normal.
 * - NO renderiza WeekNavigator, header de semana, ni otras sesiones.
 *   El atleta tiene una sola acción posible: completar el test.
 * - Texto descriptivo abajo de la card explica el porqué del gate.
 *
 * Props:
 *   session: la TrainingSession del test_vam planned (devuelta por
 *     `evaluatePlanGate` cuando `isActive=true`).
 */
import { SessionCard } from "@/components/training/SessionCard";
import { t } from "@/i18n/es";
import type { TrainingSession } from "@/types/api";

export interface PreVamGateViewProps {
  /** La sesión TEST-VAM planificada (puerta de calibración). */
  session: TrainingSession;
  /** Pre-flight F16: rodaje suave por sensaciones a hacer YA, antes del
   *  test. Si está presente se renderiza PRIMERO (es el día 1). Puede ser
   *  null en planes generados antes de F16. */
  introSession?: TrainingSession | null;
}

export function PreVamGateView({ session, introSession }: PreVamGateViewProps) {
  return (
    <section
      data-testid="pre-vam-gate-view"
      className="flex flex-col gap-4"
      aria-labelledby="pre-vam-gate-title"
    >
      <header>
        <h2
          id="pre-vam-gate-title"
          className="text-lg font-medium text-bone"
        >
          {t.plan.preVamGateTitle}
        </h2>
      </header>

      {/* Pre-flight F16: rodaje previo por sensaciones (día 1) PRIMERO — es lo
          que el atleta hace ya. Su SessionCard no muestra zona (intensity_zone
          null). Luego el TEST-VAM (día 2), que desbloquea el plan completo. */}
      {introSession && <SessionCard session={introSession} />}

      {/* SessionCard reutilizable: misma card que aparece en WeekView.
          El test_vam ya tiene badge "TEST" + estilo lime/40 propio
          (S6 Bloque 4). */}
      <SessionCard session={session} />

      <p
        data-testid="pre-vam-gate-description"
        className="text-sm text-bone-muted"
      >
        {t.plan.preVamGateDescription}
      </p>
    </section>
  );
}
